import React, { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, LayoutGrid } from "lucide-react";
import SmartImage from "../components/SmartImage";
import Reveal from "../components/Reveal";
import { assetUrl } from "../adminApi";
import Seo, { SITE } from "../components/Seo";
import { useI18n } from "../i18n";
import { useSiteData } from "../siteData";
import { DETAIL_IMAGES } from "../projectImages";
import { trackEvent } from "../tracker";

export default function ProjectDetail() {
  const { slug } = useParams();
  const { t, loc } = useI18n();
  const { projects, loading } = useSiteData();
  const navigate = useNavigate();

  const idx = projects.findIndex((p) => p.slug === slug);
  const project = projects[idx];

  useEffect(() => {
    if (project) trackEvent("project", project.slug, { once: true });
  }, [project]);

  if (loading) {
    return <div className="shell pt-[180px] pb-40 mono text-[10px] text-[#6B675F]">...</div>;
  }

  if (!project) {
    return (
      <div className="shell pt-[180px] pb-40">
        <h1 className="display text-[48px] text-[#141414]">{t("detail.notFound")}</h1>
        <Link to="/portfolio" className="mono text-[10px] text-[#141414] link-underline mt-6 inline-block">
          {t("portfolio.back")}
        </Link>
      </div>
    );
  }

  const uploaded = (project.images || []).map(assetUrl);
  const gallery =
    uploaded.length > 1
      ? uploaded.slice(1, 10)
      : [1, 2, 3].map((n) => `${DETAIL_IMAGES[(idx + n * 4) % DETAIL_IMAGES.length]}?w=900&q=80&auto=format&fit=crop`);
  const next = projects.filter((p) => p.slug !== slug).slice(0, 3);

  const Nav = () => (
    <div className="flex flex-wrap items-center gap-6">
      <button
        onClick={() => navigate(-1)}
        data-testid="detail-go-back"
        className="mono text-[9.5px] text-[#6B675F] inline-flex items-center gap-2 hover:text-[#141414] transition-colors"
      >
        <ArrowLeft size={12} strokeWidth={1.8} /> {t("cta.goBack")}
      </button>
      <Link
        to="/portfolio"
        data-testid="detail-portfolio-shortcut"
        className="mono text-[9.5px] text-[#141414] inline-flex items-center gap-2 link-underline"
      >
        <LayoutGrid size={12} strokeWidth={1.8} /> {t("nav.portfolio")}
      </Link>
    </div>
  );

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: loc(project.title),
    description: loc(project.description),
    image: uploaded[0] || undefined,
    dateCreated: project.year,
    locationCreated: { "@type": "Place", name: project.location },
    creator: { "@type": "Organization", name: "HRD Architecture", url: SITE },
    genre: project.category,
    url: `${SITE}/portfolio/${project.slug}`,
  };

  return (
    <div>
      <Seo
        path={`/portfolio/${project.slug}`}
        title={`${loc(project.title)} · ${project.category}`}
        description={(loc(project.description) || "").slice(0, 200)}
        image={uploaded[0]}
        type="article"
        jsonLd={jsonLd}
      />
      <section className="shell pt-[124px] lg:pt-[160px] pb-10">
        <Nav />
        <h1 className="display text-[30px] sm:text-[52px] lg:text-[78px] text-[#141414] mt-8 max-w-[1100px] leading-[0.98]">
          {loc(project.title)}
        </h1>
      </section>

      <section className="shell">
        <div className="media aspect-[16/9] border border-[#E2E0D8]">
          <SmartImage
            src={uploaded[0]}
            alt={loc(project.title)}
            fallback={project.fallback}
            fallbackIndex={idx}
            params="w=1800&q=82&auto=format&fit=crop"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      <section className="shell py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <dl className="lg:col-span-4 border-t border-[#E2E0D8]">
            {[
              [t("detail.client"), project.client],
              [t("detail.location"), project.location],
              [t("detail.year"), project.year],
              [t("detail.typology"), project.category],
              [t("detail.scope"), project.scope],
            ]
              .filter(([, v]) => v)
              .map(([k, v]) => (
                <div key={k} className="py-4 border-b border-[#E2E0D8]">
                  <dt className="mono text-[8.5px] text-[#A8A29A]">{k}</dt>
                  <dd className="text-[15px] text-[#141414] mt-1.5">{v}</dd>
                </div>
              ))}
          </dl>
          <div className="lg:col-span-7 lg:col-start-6">
            <p className="mono text-[9.5px] text-[#6B675F] mb-7">{t("detail.note")}</p>
            <p className="text-[18px] lg:text-[21px] leading-[1.6] text-[#141414] whitespace-pre-line">
              {loc(project.description)}
            </p>
          </div>
        </div>
      </section>

      <section className="shell pb-24 lg:pb-32">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {gallery.map((g, i) => (
            <Reveal key={`${g}-${i}`} delay={i * 90}>
              <div className="media aspect-[4/5] border border-[#E2E0D8]">
                <img
                  src={g}
                  alt={`${loc(project.title)} ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-12">
          <Nav />
        </div>
      </section>

      <section className="bg-[#F2F0EB] border-t border-[#E2E0D8] py-20 lg:py-28">
        <div className="shell">
          <div className="flex items-end justify-between gap-6 mb-12">
            <h2 className="display text-[32px] lg:text-[46px] text-[#141414]">{t("detail.next")}</h2>
            <Link to="/portfolio" className="mono text-[10px] text-[#141414] inline-flex items-center gap-2 link-underline">
              {t("cta.allProjects")} <ArrowUpRight size={13} strokeWidth={1.6} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-8">
            {next.map((p, i) => (
              <Link
                key={p.id}
                to={`/portfolio/${p.slug}`}
                className="group block bg-white border border-[#E2E0D8] hover:border-[#C8B29B] transition-colors duration-500"
              >
                <div className="media aspect-[4/3] overflow-hidden">
                  <SmartImage
                    src={assetUrl(p.images?.[0])}
                    alt={loc(p.title)}
                    fallback={p.fallback}
                    fallbackIndex={i + 4}
                    params="w=1000&q=80&auto=format&fit=crop"
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.04]"
                  />
                </div>
                <div className="p-5 lg:p-6">
                  <h3 className="text-[17px] font-medium text-[#141414] leading-snug">{loc(p.title)}</h3>
                  <p className="mono text-[9.5px] text-[#6B675F] mt-2">
                    {p.location} · {p.year}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
